import { api } from '../api/client';

/* Reusable advisory "AI Assist" helper for the Intake / FAR forms.
 * Asks the model to DRAFT specific narrative fields as a JSON object, grounded
 * in the context provided. Advisory only — the user reviews/edits before saving. */

export interface AiField {
  key: string;          // the data key to fill
  label: string;        // human label for the prompt
  kind?: 'text' | 'long' | 'list';  // shape hint
}

/**
 * Request AI-drafted values for the given fields.
 * Returns a map of key -> suggested value (string, or string[] for list fields).
 */
export async function requestAiDraft(
  sectionTitle: string,
  fields: AiField[],
  context: string,
): Promise<Record<string, any>> {
  const fieldSpec = fields.map(f => {
    const shape = f.kind === 'list' ? 'array of short strings' : f.kind === 'long' ? 'a detailed paragraph string' : 'a concise string';
    return `  "${f.key}": ${shape}  // ${f.label}`;
  }).join('\n');

  const prompt = `You are a federal contracting assistant helping DRAFT the "${sectionTitle}" section of an acquisition package. Using ONLY the context below, produce suggested draft values. This is advisory content that a contracting officer will review and edit; be specific and grounded, do not invent facts (use "[TBD]" where a value is unknown).

Return ONLY a valid JSON object with exactly these keys (no markdown, no commentary):
{
${fieldSpec}
}

CONTEXT:
${context}`;

  const res = await api.post<any>('/ai/ask', { question: prompt, context: 'draft_generation' });
  const raw = (res.answer || res.response || res.message || '').trim();
  // Extract the JSON object from the response.
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try {
      return JSON.parse(raw.slice(start, end + 1));
    } catch {
      // Fall through to empty if the model returned malformed JSON.
    }
  }
  return {};
}

export interface AiReview {
  gaps: string[];        // missing / incomplete items vs the checklist
  risks: string[];       // competition-restriction and compliance risks
  inconsistencies: string[]; // contradictions within the captured data
  summary: string;       // one-line overall readiness assessment
}

/**
 * Advisory compliance/completeness review of a completed form.
 * `checklist` is the form's final-review items; `filledContext` is the captured data.
 */
export async function requestAiReview(
  formName: string,
  checklist: string[],
  filledContext: string,
): Promise<AiReview> {
  const prompt = `You are a federal contracting compliance reviewer. Review the completed "${formName}" below against the review checklist. Identify what is missing or incomplete, any requirements that could unduly RESTRICT COMPETITION (brand names, overly specific specs, unnecessary requirements), and any internal contradictions. This is advisory; a contracting officer decides.

Return ONLY a valid JSON object (no markdown):
{
  "gaps": [array of short strings — checklist items not satisfied or fields left blank that should be filled],
  "risks": [array of short strings — competition-restriction or compliance risks, each naming the specific concern],
  "inconsistencies": [array of short strings — contradictions between captured values],
  "summary": "one-sentence overall readiness assessment"
}

REVIEW CHECKLIST:
${checklist.map((c, i) => `${i + 1}. ${c}`).join('\n')}

CAPTURED DATA:
${filledContext}`;

  const res = await api.post<any>('/ai/ask', { question: prompt, context: 'draft_generation' });
  const raw = (res.answer || res.response || res.message || '').trim();
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(raw.slice(start, end + 1));
      return {
        gaps: Array.isArray(parsed.gaps) ? parsed.gaps : [],
        risks: Array.isArray(parsed.risks) ? parsed.risks : [],
        inconsistencies: Array.isArray(parsed.inconsistencies) ? parsed.inconsistencies : [],
        summary: parsed.summary || '',
      };
    } catch { /* fall through */ }
  }
  return { gaps: [], risks: [], inconsistencies: [], summary: 'Could not parse the AI review. Try again.' };
}

/**
 * Generate structured requirement rows for the Requirements Register from the
 * mission need / outcome / functions. Returns an array of requirement objects
 * matching the register's column keys.
 */
export async function generateRequirements(context: string): Promise<any[]> {
  const prompt = `You are a federal requirements analyst. From the mission need and functions below, draft a structured Requirements Register. Produce specific, measurable, performance-based requirements. Do not invent facts beyond the context; where a metric is unknown use "[TBD]".

Return ONLY a valid JSON array (no markdown) of 5-12 requirement objects with these keys:
[{
  "title": "short requirement title",
  "description": "one-sentence requirement statement",
  "category": "one of: Functional, Performance, Technical, Security, Accessibility, Data, Integration, Delivery, Sustainability, Regulatory, Other",
  "level": "one of: Mandatory, Desired, Target",
  "type": "one of: Functional, Performance, Physical, Technical, Regulatory",
  "metric": "measurable performance metric or [TBD]",
  "acceptance": "acceptance criteria",
  "verification": "one of: Inspection, Test, Demonstration, Analysis, Document Review",
  "status": "Draft"
}]

CONTEXT:
${context}`;

  const res = await api.post<any>('/ai/ask', { question: prompt, context: 'draft_generation' });
  const raw = (res.answer || res.response || res.message || '').trim();
  const start = raw.indexOf('[');
  const end = raw.lastIndexOf(']');
  if (start >= 0 && end > start) {
    try {
      const arr = JSON.parse(raw.slice(start, end + 1));
      return Array.isArray(arr) ? arr : [];
    } catch { /* fall through */ }
  }
  return [];
}

/** Build a readable context string from an opportunity + intake + acquisition. */
export function buildOppContext(opp: any): string {
  const lines: string[] = [];
  const p = (l: string, v: any) => { if (v && String(v).trim()) lines.push(`${l}: ${v}`); };
  p('Title', opp?.title);
  p('Agency', opp?.agency);
  p('NAICS', opp?.naics_code);
  p('PSC', opp?.psc_code || opp?.product_service_code);
  p('Set-Aside', opp?.set_aside);
  p('Estimated Value', opp?.budget || opp?.budget_amount);
  p('Description', opp?.description);
  p('Notes', opp?.notes);
  try {
    const intake = opp?.intake_data ? (typeof opp.intake_data === 'string' ? JSON.parse(opp.intake_data) : opp.intake_data) : null;
    if (intake) {
      p('Intake Mission Need', intake.mission_need);
      p('Intake Intended Outcome', intake.intended_outcome);
      if (Array.isArray(intake.functions_performed)) p('Intake Functions', intake.functions_performed.filter(Boolean).join('; '));
      if (Array.isArray(intake.requirements) && intake.requirements.length) {
        lines.push('Intake Requirements: ' + intake.requirements.slice(0, 25).map((r: any) => `${r.title || ''} (${r.level || ''}/${r.type || ''})`).filter(Boolean).join('; '));
      }
    }
  } catch { /* ignore */ }
  try {
    const acq = opp?.acquisition_data ? (typeof opp.acquisition_data === 'string' ? JSON.parse(opp.acquisition_data) : opp.acquisition_data) : null;
    if (acq) {
      p('Commercial?', acq.is_commercial);
      p('Selected Method', acq.co_selected_method || acq.recommended_method);
    }
  } catch { /* ignore */ }
  return lines.join('\n');
}
